import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs';

import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { AuthActions } from '@fantaz/shared/auth';
import { ENTITY_TYPE_MAP } from '@fantaz/shared/config';
import {
  AccountService,
  BusinessGameProfileDto,
  DataAccessAccountService,
  UserGameProfileDto,
  VerificationDuplicateException,
  VerificationException,
  VerifyUserDto,
} from '@fantaz/shared/data-access/api';
import { GAEventKeys } from '@fantaz/shared/data-access/models';
import { VerifyUserActions } from '@fantaz/user-verification/data-access';

import { Store } from '@ngrx/store';
import { UserGameProfileSelector } from '.';
import * as UserGameProfileActions from './user-game-profile.actions';

@Injectable()
export class UserGameProfileEffects {
  private accountService: AccountService = inject(DataAccessAccountService);
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);

  /**
   * Effect that saves the user's profile
   */
  saveUserGameProfileEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserGameProfileActions.saveUserGameProfile),
      switchMap(({ userGameProfile }) =>
        this.accountService
          .saveUserGameProfile(
            userGameProfile.entity
              ? new BusinessGameProfileDto({
                  ...userGameProfile,
                })
              : new UserGameProfileDto({
                  ...userGameProfile,
                }),
          )
          .pipe(
            map(() =>
              UserGameProfileActions.saveUserGameProfileSuccess({
                userGameProfile,
              }),
            ),
            catchError((error) => {
              const result: any[] = [
                UserGameProfileActions.saveUserGameProfileFailure({
                  error,
                  userGameProfile,
                }),
              ];
              if (error instanceof VerificationDuplicateException) {
                result.push(
                  CoreGoogleAnalyticActions.trackEvent({
                    event: {
                      event: GAEventKeys.USER_VERIFICATION_STATUS,
                      verification_action: 'verification-duplicate',
                      verification_status: 'user-kyc-verification-fail',
                    },
                  }),
                );
              }
              return result;
            }),
          ),
      ),
    ),
  );

  /**
   * Effect to mark user such as verified after save user game profile success
   */
  showVerifyDialogEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserGameProfileActions.saveUserGameProfileSuccess),
      withLatestFrom(
        this.store.select(UserGameProfileSelector.selectRequiredVerification),
        (action, requireVerification) => ({
          ...action,
          requireVerification,
        }),
      ),
      switchMap(({ requireVerification, userGameProfile }) => [
        requireVerification
          ? VerifyUserActions.verifyUser({
              user: new VerifyUserDto({
                ...userGameProfile,
              }),
            })
          : VerifyUserActions.verifyUserSuccess({
              source: 'user-game-profile',
              requireVerification: false,
            }),
      ]),
    ),
  );

  /**
   * Effect to update user locally after save user game profile success
   */
  updateUserLocallyEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserGameProfileActions.saveUserGameProfileSuccess),
      switchMap(({ userGameProfile }) => [
        AuthActions.updateUserLocally({
          user: {
            ...userGameProfile,
            birthday: userGameProfile.dateOfBirth,
            legalAddress: userGameProfile.address,
            shippingAddress: userGameProfile.address,
            entity: ENTITY_TYPE_MAP.INDIVIDUAL,
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to report to google analytic after save user game profile success
   */
  reportSaveSuccessToGoogleAnalyticEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserGameProfileActions.saveUserGameProfileSuccess),
      switchMap(() => [
        CoreGoogleAnalyticActions.trackEvent({
          event: {
            event: 'user_verification',
            type: 'save_user_game_profile',
            state: 'success',
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to handle save user game profile failure
   */
  handleSaveUserGameProfileFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserGameProfileActions.saveUserGameProfileFailure),
      switchMap(({ error }) => {
        if (error instanceof VerificationException) {
          return [
            VerifyUserActions.verifyUserFailure({
              error,
            }),
          ];
        }

        return [
          VerifyUserActions.openVerificationDialog({}),
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: 'user_verification',
              type: 'save_user_game_profile',
              state: 'failure',
              reason: error,
            },
          }),
        ];
      }),
    ),
  );
}
